import cx from 'classnames'
import { AI } from 'blaze-ui/icons'
import { Images, LinkAngled } from 'blaze-ui/icons/30'
import Styles from './ImageButtons.module.scss'
import { Camera1 } from 'blaze-ui/icons/24'
import { forwardRef, useRef } from 'react'
import { useObjectRef } from '@react-aria/utils'
import useButtonWithScrollCancel from 'hooks/useButtonWithScrollCancel'
import { Icon } from 'blaze-ui/icons/Types'
import { ButtonProps } from 'blaze-ui/Button/Types'
import { UploadImageButtonProps } from '../Types'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { MediaFromLinkDrawer } from 'components/MediaFromLinkDrawer/MediaFromLinkDrawer'
import { GenerateImageDrawer } from 'components/GenerateImageDrawer/GenerateImageDrawer'
import { noop } from 'lodash'
import { useMediaPickerContext } from '../MediaPickerContext'

interface Props {
  capture?: 'user' | 'environment'
  icon: Icon
  label: string
  accept: string
  onSelect: (files: File[]) => void
}

const NativeImageButton = forwardRef<HTMLInputElement, Pick<ButtonProps, 'variant' | 'onPress'> & Props>(
  ({ accept, capture, icon: Icon, label, onPress, onSelect }: Pick<ButtonProps, 'onPress'> & Props, fRef) => {
    const ref = useObjectRef(fRef)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const { buttonProps, isPressed } = useButtonWithScrollCancel(
      {
        onPress: (e) => {
          if (onPress) {
            onPress(e)
          } else {
            fileInputRef.current?.click()
          }
        },
        elementType: 'div',
      },
      ref,
    )

    return (
      <div {...buttonProps} className={cx(Styles.button, { [Styles.pressed]: isPressed })} ref={ref}>
        <Icon size={28} color="var(--selected-stroke)" />
        {!onPress && (
          <input
            className={Styles.fileInput}
            aria-label={label}
            ref={fileInputRef}
            type="file"
            onChange={(event) => {
              const files = Array.from(event.target.files || [])
              onSelect(files)
            }}
            accept={accept}
            capture={capture}
          />
        )}
        <div className={Styles.buttonLabel}>{label}</div>
      </div>
    )
  },
)

export const ImageButtons = observer(({ accept }: UploadImageButtonProps) => {
  const { drawersStore } = useRootStore()
  const { handleUpload } = useMediaPickerContext()

  const openLinkDrawer = () => {
    drawersStore.openDrawer(MediaFromLinkDrawer, {
      accept: accept,
      onUse: (file) => {
        handleUpload([file])
      },
    })
  }

  const openGenerateDrawer = () => {
    drawersStore.openDrawer(GenerateImageDrawer, {
      onUse: async (img) => {
        const response = await fetch(img.url)
        const blob = await response.blob()
        const file = new File([blob], 'AI Generated Image', { type: img.mimeType })
        handleUpload([file])
      },
    })
  }

  return (
    <div className={Styles.buttonsGrid}>
      <NativeImageButton onSelect={handleUpload} accept={accept} icon={Images} label="Files" />
      <NativeImageButton onSelect={handleUpload} accept={accept} icon={Camera1} label="Camera" capture="environment" />
      <NativeImageButton onSelect={noop} accept={accept} icon={AI} label="Generate" onPress={openGenerateDrawer} />
      <NativeImageButton
        onSelect={noop}
        accept={accept}
        icon={LinkAngled}
        label="Paste Link"
        onPress={openLinkDrawer}
      />
    </div>
  )
})
